/**
 * Grouped Bar Chart Type Definition
 */

import {
  paletteControl,
  numCategoriesControl,
  numSeriesControl,
  barOrderingControl,
  labelRotationControl,
  truncateXAxisLabelsControl,
  gridControl,
  showValuesControl,
  valueDecimalsControl,
  axisControls,
  referenceLineControls,
} from '../shared/controls';

import {
  matplotlibSetup,
  createFigure,
  titleCode,
  gridCode,
  axisLabelsCode,
  referenceLineCode,
  labelRotationCode,
  spineCode,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
  seabornCreateFigure,
  seabornTitleCode,
  seabornGridCode,
  seabornAxisLabelsCode,
  seabornYAxisRangeCode,
  seabornReferenceLineCode,
  seabornLabelRotationCode,
  seabornSpineCode,
  seabornFinishCode,
} from '../shared/seabornSnippets';

import { getColors } from '../shared/palettes';
import { SERIES_HATCHES_MPL } from '../../utils/accessibility';

const showLegendControl = {
  key: 'showLegend',
  label: 'Show Legend',
  type: 'toggle',
};

const groupedBar = {
  id: 'grouped-bar',
  name: 'Grouped Bar Chart',
  category: 'bar',
  description: 'Compare multiple metrics across categories',
  
  sampleData: [
    { name: 'Group 1', series1: 78.2, series2: 79.5, series3: 77.8 },
    { name: 'Group 2', series1: 85.3, series2: 86.1, series3: 84.9 },
    { name: 'Group 3', series1: 88.7, series2: 89.2, series3: 88.3 },
    { name: 'Group 4', series1: 90.5, series2: 91.1, series3: 90.2 },
  ],
  
  defaultConfig: {
    externalColorMode: 'palette',
    useBluesPalette: true,
    useRedsPalette: false,
    useGreensPalette: false,
    numCategories: 4,
    numSeries: 3,
    outputFormat: 'matplotlib',
    labelRotation: null,
    truncateXAxisLabels: false,
    showGrid: true,
    showValues: false,
    valueDecimals: 1,
    ylabel: 'Value',
    xlabel: 'Category',
    title: '',
    yMin: '',
    yMax: '',
    showReferenceLine: false,
    referenceValue: '',
    referenceLabel: 'Baseline',
    barOrdering: 'original',
    usePatternFills: false,
    showLegend: true,
    colorOverrides: undefined,
  },
  
  controls: [
    paletteControl,
    numCategoriesControl,
    numSeriesControl,
    barOrderingControl,
    labelRotationControl,
    truncateXAxisLabelsControl,
    gridControl,
    showValuesControl,
    valueDecimalsControl,
    {
      key: 'usePatternFills',
      label: 'Use Pattern Fills',
      type: 'toggle',
    },
    showLegendControl,
    ...axisControls,
    ...referenceLineControls,
  ],
  
  generateCode: (config, dataContext, s) => {
    const hasWideUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.inputShape === 'wide' && Array.isArray(dataContext.mapping?.valueColumns) && dataContext.mapping.valueColumns.length > 0;
    const hasLongUserData = dataContext?.isLoaded && dataContext.mapping?.category && dataContext.mapping?.value && dataContext.mapping?.series;
    const hasUserData = hasWideUserData || hasLongUserData;
    const hasYMin = config.yMin && config.yMin.trim() !== '';
    const hasYMax = config.yMax && config.yMax.trim() !== '';
    const numCategories = config.numCategories || 4;
    const numSeries = config.numSeries || 3;
    
    const colors = getColors(config, hasWideUserData ? dataContext.mapping.valueColumns.length : numSeries, s);
    const hatches = JSON.stringify(SERIES_HATCHES_MPL);
    const usePatternFills = !!config.usePatternFills;
    
    // Generate category labels
    const categories = Array.from({ length: numCategories }, (_, i) => `Group ${i + 1}`);
    
    // Seaborn output - uses long-form DataFrame (much cleaner!)
    if (config.outputFormat === 'seaborn') {
      const seabornOrderingCode = config.barOrdering === 'ascending'
        ? `
# Order groups by average value (ascending)
group_order = df.groupby('group')['value'].mean().sort_values(ascending=True).index.tolist()
df['group'] = pd.Categorical(df['group'], categories=group_order, ordered=True)
df = df.sort_values('group')`
        : config.barOrdering === 'descending'
          ? `
# Order groups by average value (descending)
group_order = df.groupby('group')['value'].mean().sort_values(ascending=False).index.tolist()
df['group'] = pd.Categorical(df['group'], categories=group_order, ordered=True)
df = df.sort_values('group')`
          : '';
      const seabornOrderArg = config.barOrdering === 'original' ? '' : ', order=group_order';

      // Generate data rows for long-form DataFrame
      const dataRows = [];
      for (let i = 0; i < numCategories; i++) {
        for (let j = 0; j < numSeries; j++) {
          const value = (75 + Math.random() * 20).toFixed(1);
          dataRows.push(`    {'group': 'Group ${i + 1}', 'series': 'Series ${j + 1}', 'value': ${value}}`);
        }
      }
      
      return `${seabornSetup(s)}

# ======== DATA ========
${hasUserData ? (hasWideUserData
  ? `df = pd.read_csv('${dataContext.fileName}')
df = df.melt(id_vars=['${dataContext.mapping.category}'], value_vars=${JSON.stringify(dataContext.mapping.valueColumns)},
             var_name='series', value_name='value')
df = df.rename(columns={'${dataContext.mapping.category}': 'group'})`
  : `df = pd.read_csv('${dataContext.fileName}')
df = df.rename(columns={'${dataContext.mapping.category}': 'group', '${dataContext.mapping.series}': 'series', '${dataContext.mapping.value}': 'value'})`) : `# Long-form DataFrame (each row is one observation)
df = pd.DataFrame([
${dataRows.join(',\n')}
])`}
${seabornOrderingCode}
# ======================
${seabornCreateFigure(5.5, 4.5, 300, s)}

# Active chart palette (${numSeries} interpolated colors)
palette = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

# Create grouped bar chart
sns.barplot(data=df, x='group', y='value', hue='series', palette=palette${seabornOrderArg},
            edgecolor='${s?.axisColor || '#000000'}', linewidth=1.0, ax=ax)
${config.usePatternFills ? `# Add non-color hatching by series
for i, container in enumerate(ax.containers):
    for bar in container:
        bar.set_hatch(hatch_patterns[i % len(hatch_patterns)])` : '# Pattern fills disabled'}
${seabornTitleCode(config, s)}
${seabornAxisLabelsCode(config, s)}
${seabornLabelRotationCode(config)}

# Add grid
${seabornGridCode(config, 'y', s)}

${config.showValues ? `# Add value labels on bars
for container in ax.containers:
    for bar in container:
        height = bar.get_height()
        txt_color = _coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}')
        ax.text(bar.get_x() + bar.get_width()/2., height,
                f'{height:.${config.valueDecimals || 1}f}',
                ha='center', va='bottom', fontsize=${s?.valueLabelSize || 12}, color=txt_color)` : '# Value labels disabled'}

# Set y-axis range
${seabornYAxisRangeCode(config)}

${config.showLegend ? `# Customize legend
ax.legend(title='', loc='upper right', fontsize=${s?.legendSize || 10})` : `# Remove legend
ax.legend_.remove()`}
${seabornReferenceLineCode(config, 'horizontal', s)}
${seabornSpineCode(s, 'label', 'number')}
${seabornFinishCode('grouped_bar_chart')}`;
    }
    
    // Matplotlib output (default)
    // Generate series data
    const seriesCode = Array.from({ length: numSeries }, (_, i) => {
      const values = Array.from({ length: numCategories }, () => (75 + Math.random() * 20).toFixed(1));
      return `series${i + 1} = [${values.join(', ')}]  # Series ${i + 1} values`;
    }).join('\n');
    
    // Generate bar creation code
    const width = (0.8 / numSeries).toFixed(2);
    const barCodes = Array.from({ length: numSeries }, (_, i) => {
      const offset = ((i - (numSeries - 1) / 2) * parseFloat(width)).toFixed(2);
      return `bars${i + 1} = ax.bar(x + ${offset}, series${i + 1}, ${width}, label='Series ${i + 1}', color=colors[${i}], hatch=(hatch_patterns[${i} % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))`;
    }).join('\n');
    const sampleOrderingCode = config.barOrdering === 'ascending'
      ? `# Order categories by average value across series (ascending)
group_means = np.mean(np.array([${Array.from({ length: numSeries }, (_, i) => `series${i + 1}`).join(', ')}]), axis=0)
sorted_indices = np.argsort(group_means)
categories = [categories[i] for i in sorted_indices]
${Array.from({ length: numSeries }, (_, i) => `series${i + 1} = [series${i + 1}[i] for i in sorted_indices]`).join('\n')}`
      : config.barOrdering === 'descending'
        ? `# Order categories by average value across series (descending)
group_means = np.mean(np.array([${Array.from({ length: numSeries }, (_, i) => `series${i + 1}`).join(', ')}]), axis=0)
sorted_indices = np.argsort(group_means)[::-1]
categories = [categories[i] for i in sorted_indices]
${Array.from({ length: numSeries }, (_, i) => `series${i + 1} = [series${i + 1}[i] for i in sorted_indices]`).join('\n')}`
        : '# Keep original category ordering';
    
    // Generate series list for value labels
    const barsList = Array.from({ length: numSeries }, (_, i) => `bars${i + 1}`).join(', ');

    if (hasUserData) {
      // For user data, generate a cleaner pivot-based matplotlib approach
      return `${matplotlibSetup(s)}

# ======== DATA ========
df = pd.read_csv('${dataContext.fileName}')
${hasWideUserData
  ? `pivot = df.set_index('${dataContext.mapping.category}')[${JSON.stringify(dataContext.mapping.valueColumns)}]`
  : `pivot = df.pivot_table(index='${dataContext.mapping.category}', columns='${dataContext.mapping.series}', values='${dataContext.mapping.value}', aggfunc='first')`}
${config.barOrdering === 'ascending'
  ? `# Order groups by average value (ascending)
pivot = pivot.loc[pivot.mean(axis=1).sort_values(ascending=True).index]`
  : config.barOrdering === 'descending'
    ? `# Order groups by average value (descending)
pivot = pivot.loc[pivot.mean(axis=1).sort_values(ascending=False).index]`
    : '# Keep original group ordering'}
categories = pivot.index.tolist()
series_names = pivot.columns.tolist()
# ======================
${createFigure(5.5, 4.5, 300, s)}

# Active chart palette
colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

# Set up bar positions
x = np.arange(len(categories))
width = ${(0.8 / numSeries).toFixed(2)}
n_series = len(series_names)
bar_width = 0.8 / n_series

# Create grouped bars
for idx, series_name in enumerate(series_names):
    offset = (idx - (n_series - 1) / 2) * bar_width
    ax.bar(x + offset, pivot[series_name].values, bar_width, label=series_name, color=colors[idx % len(colors)],
           hatch=(hatch_patterns[idx % len(hatch_patterns)] if ${usePatternFills ? 'True' : 'False'} else ''))

${config.showValues ? `# Add value labels on bars
for container in ax.containers:
    for bar in container:
        height = bar.get_height()
        ax.text(bar.get_x() + bar.get_width()/2., height,
                f'{height:.${config.valueDecimals || 1}f}',
                ha='center', va='bottom', fontsize=${s?.valueLabelSize || 12},
                color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}

# Set x-axis ticks
ax.set_xticks(x)
ax.set_xticklabels(categories)
${labelRotationCode(config)}

# Add grid
${gridCode(config, 'y', s)}

# Set y-axis range
${hasYMin || hasYMax ? `ax.set_ylim(${hasYMin ? `bottom=${config.yMin}` : ''}${hasYMin && hasYMax ? ', ' : ''}${hasYMax ? `top=${config.yMax}` : ''})` : '# Auto y-axis range'}

${config.showLegend ? `# Add legend
ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'horizontal', s)}
${spineCode(s, 'label', 'number')}
${finishCode('grouped_bar_chart')}`;
    }

    return `${matplotlibSetup(s)}

# ======== DATA ========
categories = ${JSON.stringify(categories)}  # Group labels
${seriesCode}
# ======================
${sampleOrderingCode}
${createFigure(5.5, 4.5, 300, s)}

# Active chart palette (${numSeries} interpolated colors)
colors = ${JSON.stringify(colors)}
hatch_patterns = ${hatches}

# Set up bar positions
x = np.arange(len(categories))

# Create grouped bars
${barCodes}

${config.showValues ? `# Add value labels on bars
for bars in [${barsList}]:
    for bar in bars:
        height = bar.get_height()
        ax.text(bar.get_x() + bar.get_width()/2., height,
                f'{height:.${config.valueDecimals || 1}f}',
                ha='center', va='bottom', fontsize=${s?.valueLabelSize || 12},
                color=_coplot_text_color_for_rgba(bar.get_facecolor(), preferred='${s?.foreground || '#000000'}'))` : '# Value labels disabled'}
${titleCode(config, s)}
${axisLabelsCode(config, s)}

# Set x-axis ticks
ax.set_xticks(x)
ax.set_xticklabels(categories)
${labelRotationCode(config)}

# Add grid
${gridCode(config, 'y', s)}

# Set y-axis range
${hasYMin || hasYMax ? `ax.set_ylim(${hasYMin ? `bottom=${config.yMin}` : ''}${hasYMin && hasYMax ? ', ' : ''}${hasYMax ? `top=${config.yMax}` : ''})` : '# Auto y-axis range'}

${config.showLegend ? `# Add legend
ax.legend(loc='upper right', fontsize=${s?.legendSize || 10})` : '# Legend disabled'}
${referenceLineCode(config, 'horizontal', s)}
${spineCode(s, 'label', 'number')}
${finishCode('grouped_bar_chart')}`;
  },
};

export default groupedBar;
